import { PermissionsService } from '../../../../../services/core/permissions.service';
import { Component, ChangeDetectionStrategy, inject, computed, OnInit, Inject, LOCALE_ID, signal, DestroyRef, effect } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CommonModule, formatDate } from '@angular/common';
import { ResumeDataService } from '../../services/resume-data.service';
import { WidgetsModule } from '../../../../../shared/widgets.module';
// Tier 2.3: WjGridModule removed — replaced by KiteGridComponent (provided by WidgetsModule).
// Inline editing moved from Wijmo `<ng-template wjFlexGridCellTemplate [cellType]="'CellEdit'">`
// to AG Grid `cellEditor` properties (agSelectCellEditor / agNumberCellEditor).
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EcoClientsService } from '../../../../../services/eco/eco-clients.service';
import { ToastrService } from 'ngx-toastr';
import { CustomerTypesEnum } from '../../../../../constants/customers.enum';
import { AssignedBenefitsModalComponent } from './modals/assigned-benefits-modal/assigned-benefits-modal.component';
import { ECO_FREQUENCY_OPTIONS, ECO_LOCATION_TYPES, ECO_PRICE_TYPE_OPTIONS, ECO_TIME_SLOTS, ECO_DURATION_OPTIONS } from '../../../../../constants/eco.constants';
import { DialogsService } from '../../../../../services/dialogs.service';

@Component({
    selector: 'app-eco-info-tab',
    imports: [CommonModule, WidgetsModule, ReactiveFormsModule, FormsModule
        // WjGridModule removed (Tier 2.3).
    ],
    templateUrl: './eco-info-tab.component.html',
    styleUrls: ['./eco-info-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EcoInfoTabComponent implements OnInit {
  readonly permissions = inject(PermissionsService);

  private readonly resumeData = inject(ResumeDataService);
  private readonly ecoClientsService = inject(EcoClientsService);
  private readonly toastr = inject(ToastrService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly modalService = inject(NgbModal);
  private readonly fb = inject(FormBuilder);
  private readonly modalsService = inject(DialogsService);

  public editTrapsMode = signal(false);
  public editAgreementMode = signal(false);
  public savingTraps = signal(false);
  public savingAgreement = signal(false);

  public agreementForm: FormGroup = this.fb.group({
    agreement_type: [''],
    duration: [0],
    price: [0],
    start_date: [null],
    end_date: [null],
  });

  constructor(@Inject(LOCALE_ID) private locale: string) {
    effect(() => {
      const identGui = this.clientData()?.ident_GUI;
      const alreadyLoaded = this.resumeData.ecoInformationTabData();
      const isLoading = this.resumeData.ecoInformationLoading();
      const hasFailed = this.resumeData.ecoInformationFailed();

      if (identGui && !alreadyLoaded && !isLoading && !hasFailed) {
        this.resumeData.fetchEcoInformationData();
      }

      if (alreadyLoaded?.ecoClean) {
        this.ecoCleanItems.set([...alreadyLoaded.ecoClean]);
      }

      const ecoId = alreadyLoaded?._id;
      if (ecoId && !this.loadingEquipment()) {
        this.isMigrated.set(alreadyLoaded.clean_equipment !== undefined);
        this.cleanEquipment.set(alreadyLoaded.clean_equipment || []);
      }
    }, { allowSignalWrites: true });
  }

  public readonly clientData = computed(() => this.resumeData.clientInfomationTabData());
  readonly ecoData = computed(() => this.resumeData.ecoInformationTabData());

  public ecoCleanItems = signal<any[]>([]);
  readonly cleanEquipment   = signal<any[]>([]);
  readonly loadingEquipment = signal(false);
  readonly isMigrated       = signal(false);

  readonly ecoFinancial = computed<any | null>(() => {
    const data = this.ecoData();
    return data?.ecoFinancial || null;
  });

  readonly ecoBenefits = computed<any[]>(() => {
    const financial = this.ecoFinancial();
    return financial?.benefits && Array.isArray(financial.benefits) ? financial.benefits : [];
  });

  readonly loading = computed(() => this.resumeData.ecoInformationLoading());
  public downloading = signal(false);
  public selectedCleanItem = signal<any | null>(null);

  public ECO_LOCATION_TYPES = ECO_LOCATION_TYPES;
  public ECO_PRICE_TYPE_OPTIONS = ECO_PRICE_TYPE_OPTIONS;
  public ECO_FREQUENCY_OPTIONS = ECO_FREQUENCY_OPTIONS;
  public ECO_TIME_SLOTS = ECO_TIME_SLOTS;
  public ECO_DURATION_OPTIONS = ECO_DURATION_OPTIONS;

  // Tier 2.3: Column definitions built per `editTrapsMode` state.
  /** Read-only column defs (view mode). */
  private readonly readonlyColumnDefs: any[] = [
    { field: 'location_type', headerName: 'Location', width: 130,
      valueFormatter: (p: any) => this.getLocationName(p.value) },
    { field: 'location_description', headerName: 'Description', flex: 1.5,
      cellClass: 'ag-cell-left' },
    { field: 'price_type', headerName: 'Price Type', width: 130,
      valueFormatter: (p: any) => this.getPriceTypeName(p.value) },
    { field: 'price', headerName: 'Price', width: 100,
      cellClass: 'ag-cell-right',
      cellRenderer: 'currencyRenderer',
      cellRendererParams: { format: 'USD', digits: '1.2-2' } },
    { field: 'gallons', headerName: 'Gallons', width: 100, cellClass: 'ag-cell-center' },
    { field: 'time_available_from', headerName: 'Time From', width: 140,
      cellClass: 'ag-cell-center',
      valueFormatter: (p: any) => p.value ?? '—' },
    { field: 'time_available_to', headerName: 'Time To', width: 140,
      cellClass: 'ag-cell-center',
      valueFormatter: (p: any) => p.value ?? '—' },
    { field: 'frequency_service', headerName: 'Frequency', width: 140,
      valueFormatter: (p: any) => this.getFrequencyName(p.value) },
    { field: 'lids_count', headerName: 'Lids', width: 80, cellClass: 'ag-cell-center' }
  ];

  /** Editable column defs (edit mode). Adds cellEditor + editable:true/false.
   *  Note: AG Grid Community supports one cellEditor per column; the original
   *  Wijmo `Time` column had TWO selects in the same column (from + to).
   *  We preserve functionality by splitting into two columns. */
  private readonly editableColumnDefs: any[] = this.readonlyColumnDefs.map((col: any) => ({
    ...col,
    editable: true
  })).map((col: any) => {
    // Inject select editors for known code/option columns.
    switch (col.field) {
      case 'location_type':
        return {
          ...col,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: { values: ECO_LOCATION_TYPES.map(x => x.code) }
        };
      case 'price_type':
        return {
          ...col,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: { values: ECO_PRICE_TYPE_OPTIONS.map(x => x.code) }
        };
      case 'time_available_from':
      case 'time_available_to':
        return {
          ...col,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: { values: ECO_TIME_SLOTS.map(x => x.code) }
        };
      case 'frequency_service':
        return {
          ...col,
          cellEditor: 'agSelectCellEditor',
          cellEditorParams: { values: ECO_FREQUENCY_OPTIONS.map(x => x.code) }
        };
      default:
        return col;
    }
  });

  /** Computed: switches columnDefs based on edit mode. */
  columnDefs = computed<any[]>(() => this.editTrapsMode() ? this.editableColumnDefs : this.readonlyColumnDefs);

  CustomerTypesEnum = CustomerTypesEnum;

  ngOnInit(): void {}

  onEquipmentChanged(): void {
    this.resumeData.fetchEcoInformationData();
  }

  openCreateEcoAgreement(): void {
    if (!this.permissions.canCreateEcoClient()) return;
    const client = this.clientData();
    const ucoClientId = client?._id || client?.ident_GUI;
    if (!ucoClientId) return;

    this.modalsService.openEcoClientOnboardingWizard({ preSelectedUcoClientId: ucoClientId })
      .then((modalRef: any) => modalRef?.result)
      .then((res: any) => {
        if (res) {
          this.resumeData.fetchEcoInformationData();
        }
      })
      .catch(() => { });
  }

  getLocationName(code: string): string {
    return ECO_LOCATION_TYPES.find(x => x.code === code)?.name || code || '';
  }

  getFrequencyName(code: string): string {
    return ECO_FREQUENCY_OPTIONS.find(x => x.code === code)?.name || code || '';
  }

  getPriceTypeName(code: string): string {
    return ECO_PRICE_TYPE_OPTIONS.find(x => x.code === code)?.name || code || '';
  }

  formatDates(dates: Date[] | string[]): string {
    if (!dates || dates.length === 0) return '';
    return dates.map(d => formatDate(d, 'MMM d, y', this.locale, 'UTC')).join(', ');
  }

  formatDate(date: string | null | undefined): string {
    if (!date) return '—';
    return formatDate(date, 'MMM d, y', this.locale, 'UTC');
  }

  formatDuration(months: number | string | null | undefined): string {
    if (!months) return '—';
    const m = Number(months);
    if (isNaN(m)) return String(months);

    if (m === 0) return '0 Months';

    const years = Math.floor(m / 12);
    const remainingMonths = m % 12;

    const parts: string[] = [];
    if (years > 0) {
      parts.push(`${years} ${years === 1 ? 'Year' : 'Years'}`);
    }
    if (remainingMonths > 0) {
      parts.push(`${remainingMonths} ${remainingMonths === 1 ? 'Month' : 'Months'}`);
    }

    return parts.join(' and ');
  }

  downloadAgreement(): void {
    const clientId = this.ecoData()?._id;
    if (!clientId) {
      this.toastr.warning('Please select a client');
      return;
    }
    this.downloading.set(true);
    this.ecoClientsService.downloadAgreement(clientId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const blob = new Blob([response], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${this.resumeData.clientInfomationTabData()?.name_client}_agreement.pdf`;
          link.click();
          window.URL.revokeObjectURL(url);
          this.toastr.success('Agreement downloaded successfully');
          this.downloading.set(false);
        },
        error: () => {
          this.toastr.error('Error downloading agreement');
          this.downloading.set(false);
        }
      });
  }

  openBenefitsModal(): void {
    const modalRef = this.modalService.open(AssignedBenefitsModalComponent, { size: 'lg', centered: true });

    const traps = this.ecoCleanItems();
    const capacity = traps.reduce((acc, curr) => acc + (Number(curr.gallons) || 0), 0);

    modalRef.componentInstance.ecoBenefits = JSON.parse(JSON.stringify(this.ecoBenefits()));
    modalRef.componentInstance.trapQuantity = traps.length;
    modalRef.componentInstance.capacity = capacity;
    modalRef.componentInstance.restaurantCount = 1;

    modalRef.result.then((updatedBenefits) => {
      if (updatedBenefits) {
        this.saveUpdatedBenefits(updatedBenefits);
      }
    }).catch(() => {});
  }

  saveUpdatedBenefits(benefits: any[]): void {
    const clientId = this.ecoData()?._id;
    if (!clientId) return;

    this.ecoClientsService.updateEcoFinancial(clientId, { benefits })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.modalsService.openFeedbackSuccess({ title: 'Benefits Updated', msg: 'Benefits updated successfully' });
          this.resumeData.fetchEcoInformationData();
        },
        error: () => {
          this.modalsService.openFeedbackError({ title: 'Update Failed', msg: 'Error updating benefits' });
        }
      });
  }

  /** Tier 2.3: replaces `onSelectionChanged(grid)` — AG Grid `(rowClicked)`
   *  output provides `event.data` directly. */
  onRowClicked(event: any): void {
    if (event?.data) {
      this.selectedCleanItem.set(event.data);
    }
  }

  /** Tier 2.3: replaces `onCellEditEnded(grid, e)`. AG Grid `(cellValueChanged)`
   *  output provides `event.colDef.field` and `event.data` directly — no
   *  `grid.columns[e.col].binding` lookup or hit-testing required. */
  onCellValueChanged(event: any): void {
    if (event?.colDef?.field !== 'lids_count') return;
    const item = event.data;
    if (!item) return;
    const count = Math.max(0, parseInt(item.lids_count) || 0);

    if (!item.lids_descriptions) item.lids_descriptions = [];

    while (item.lids_descriptions.length < count) {
      item.lids_descriptions.push('');
    }
    while (item.lids_descriptions.length > count) {
      item.lids_descriptions.pop();
    }

    this.selectedCleanItem.set({ ...item });
  }

  toggleEditAgreement(): void {
    if (this.editAgreementMode()) {
      this.editAgreementMode.set(false);
    } else {
      const financial = this.ecoFinancial();
      if (financial) {
        this.agreementForm.patchValue({
          agreement_type: financial.agreement_type,
          duration: Number(financial.duration),
          price: financial.price,
          start_date: financial.start_date ? new Date(financial.start_date).toISOString().substring(0, 10) : null,
          end_date: financial.end_date ? new Date(financial.end_date).toISOString().substring(0, 10) : null
        });
      }
      this.editAgreementMode.set(true);
    }
  }

  saveAgreement(): void {
    const clientId = this.ecoData()?._id;
    if (!clientId) return;

    this.savingAgreement.set(true);
    this.ecoClientsService.updateEcoFinancial(clientId, this.agreementForm.value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.modalsService.openFeedbackSuccess({ title: 'Agreement Updated', msg: 'Agreement updated successfully' });
          this.editAgreementMode.set(false);
          this.resumeData.fetchEcoInformationData();
          this.savingAgreement.set(false);
        },
        error: () => {
          this.modalsService.openFeedbackError({ title: 'Update Failed', msg: 'Error updating agreement' });
          this.savingAgreement.set(false);
        }
      });
  }

  toggleEditTraps(): void {
    this.editTrapsMode.update(v => !v);
  }

  saveTraps(): void {
    const clientId = this.ecoData()?._id;
    if (!clientId) return;

    this.savingTraps.set(true);
    // Tier 2.3: since editing happens via AG Grid `(cellValueChanged)`, the
    // `ecoCleanItems` signal reflects all current edits. Send it as-is.
    this.ecoClientsService.updateEcoClean(clientId, this.ecoCleanItems())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.modalsService.openFeedbackSuccess({ title: 'Traps Updated', msg: 'Traps updated successfully' });
          this.editTrapsMode.set(false);
          this.resumeData.fetchEcoInformationData();
          this.savingTraps.set(false);
        },
        error: () => {
          this.modalsService.openFeedbackError({ title: 'Update Failed', msg: 'Error updating traps' });
          this.savingTraps.set(false);
        }
      });
  }
}
